/**
 * GrowthCEODashboard — Premium SaaS dashboard
 * Hero card + secondary metrics grid (Shopify/Stripe style)
 */
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Users, ShoppingCart, BarChart3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const TrendBadge = ({ value, trend }) => {
  if (trend === "up") {
    return (
      <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2.5 py-1 rounded-md text-sm font-semibold">
        <ArrowUpRight className="w-4 h-4" />
        <span>+{value}%</span>
      </div>
    );
  }
  if (trend === "down") {
    return (
      <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2.5 py-1 rounded-md text-sm font-semibold">
        <ArrowDownRight className="w-4 h-4" />
        <span>{value}%</span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 bg-slate-200 text-slate-700 px-2.5 py-1 rounded-md text-sm font-semibold">
      <span>{value}%</span>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, formatted, change, trend }) => {
  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">{label}</p>
            <p className="text-2xl font-bold text-slate-900">{formatted}</p>
            {change !== undefined && (
              <div className="mt-3">
                <TrendBadge value={Math.abs(change)} trend={trend} />
              </div>
            )}
          </div>
          {Icon && (
            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
              <Icon className="w-6 h-6 text-slate-600" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function GrowthCEODashboard({ authAxios, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/ceo?period=${period}`);
        setData(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching CEO dashboard:", err);
        toast.error("Erro ao carregar dados");
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [period, authAxios]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Hero skeleton */}
        <div className="rounded-xl bg-gradient-to-br from-slate-200 to-slate-300 p-8 space-y-6">
          <div>
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-16 w-64 mb-4" />
          </div>
          <div className="flex flex-col sm:flex-row gap-6 pt-4 border-t border-slate-300">
            <div className="flex-1">
              <Skeleton className="h-3 w-20 mb-2" />
              <Skeleton className="h-8 w-24" />
            </div>
            <div className="flex-1">
              <Skeleton className="h-3 w-20 mb-2" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
        </div>
        {/* Metrics grid skeleton */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-20 mb-3" />
                  <Skeleton className="h-6 w-16" />
                </CardContent>
              </Card>
            ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <p className="text-red-700 font-semibold">Erro ao carregar dados</p>
          <p className="text-sm text-red-600 mt-1">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const { gross_revenue, total_orders, avg_ticket, new_customers, returning_customers } = data;

  return (
    <div className="space-y-6">
      {/* Hero Card — Main Revenue Metric */}
      <div className="rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white border border-slate-700/50 shadow-lg">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <p className="text-sm font-semibold text-slate-300 uppercase tracking-widest mb-2">{gross_revenue.label}</p>
            <h1 className="text-6xl font-bold tracking-tight">{gross_revenue.formatted}</h1>
          </div>

          {/* Sub-metrics and trends */}
          <div className="flex flex-col sm:flex-row gap-6 pt-4 border-t border-slate-700/50">
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-2">Pedidos</p>
              <div className="flex items-baseline gap-3">
                <p className="text-3xl font-bold">{total_orders.formatted}</p>
                {total_orders.change_percent !== undefined && (
                  <TrendBadge value={Math.abs(total_orders.change_percent)} trend={total_orders.trend} />
                )}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-2">Ticket Médio</p>
              <div className="flex items-baseline gap-3">
                <p className="text-3xl font-bold">{avg_ticket.formatted}</p>
                {avg_ticket.change_percent !== undefined && (
                  <TrendBadge value={Math.abs(avg_ticket.change_percent)} trend={avg_ticket.trend} />
                )}
              </div>
            </div>
            {gross_revenue.change_percent !== undefined && (
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-2">Crescimento</p>
                <div className="flex items-baseline gap-3">
                  <p className="text-xl font-semibold">Receita</p>
                  <TrendBadge value={Math.abs(gross_revenue.change_percent)} trend={gross_revenue.trend} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Metrics Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Users}
          label="Clientes Novos"
          value={new_customers.value}
          formatted={new_customers.formatted}
        />
        <MetricCard
          icon={ShoppingCart}
          label="Clientes Recorrentes"
          value={returning_customers.value}
          formatted={returning_customers.formatted}
        />
        <MetricCard
          icon={BarChart3}
          label="Pedidos/Dia"
          formatted={(total_orders.value / (parseInt(period) || 30)).toFixed(1)}
        />
        <MetricCard
          icon={TrendingUp}
          label="Saúde Geral"
          formatted="Excelente"
        />
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-900">
            💡 <strong>Dica:</strong> Explore <strong>Vendas</strong> para gráficos de receita,
            <strong> Canais</strong> para origem de vendas, <strong>Produtos</strong> para ranking, e
            <strong> Clientes</strong> para análise comportamental.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
